import React, { useCallback, useEffect, useRef, useState } from "react";
import { GridReadyEvent } from "ag-grid-community";
import { AgGridColumn, AgGridReact } from "ag-grid-react";
import { dollarize } from "../../../../../../../../utils/commonFunctions/dollar";
import { dateTimeZoneConverter } from "../../../../../../../../utils/commonFunctions/date";
import { ClaimLinesProps } from "../../../../../../../../@types/components/EOB.types";

type DateCellRendererProps = {
	value: string;
};

type AmountCellRenderer = {
	value: number;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const remarkCodevalueGetter = (params: { data: { EXPLCD01: any; EXPLCD02: any; EXPLCD03: any } }): string => {
	const { EXPLCD01, EXPLCD02, EXPLCD03 } = params.data;
	let remarkCode = "";
	if (EXPLCD01) remarkCode = remarkCode + EXPLCD01;
	if (EXPLCD02) remarkCode = remarkCode + ", " + EXPLCD02;
	if (EXPLCD03) remarkCode = remarkCode + ", " + EXPLCD03;
	return remarkCode;
};

const notCoveredValueGetter = (params: {
	data: { EERSP: any; DEDAMT: any; COPMT: any; COINAMT: any; not_covered: any };
}): number => {
	if (params.data.not_covered) {
		return params.data.not_covered;
	}
	const { EERSP, DEDAMT, COPMT, COINAMT } = params.data;
	const notCovered = EERSP - DEDAMT - COPMT - COINAMT;
	return notCovered;
};

const ExplanationOfBenefitsGrid: React.FC<ClaimLinesProps> = ({ claimLines }): JSX.Element => {
	const [bottomRowData, setBottomRowDate] = useState<any>(null);
	const agGridRef = useRef<any>(null);

	const handleAgGridReady = useCallback(
		(params: GridReadyEvent) => {
			const { api } = params;
			api.sizeColumnsToFit();
			if (bottomRowData) api.setPinnedBottomRowData([bottomRowData]);
		},
		[bottomRowData]
	);

	const dateCellRenderer = ({ value }: DateCellRendererProps) => {
		return <div>{value ? String(dateTimeZoneConverter(value)) : "N/A"}</div>;
	};

	const amountCellRenderer = ({ value }: AmountCellRenderer) => {
		// console.log("value", value);
		return <div>{value ? dollarize(value) : "$0.00"}</div>;
	};

	useEffect(() => {
		let not_covered: number | string = 0,
			discount: number | string = 0,
			total_charge: number | string = 0,
			eligible_expense: number | string = 0,
			deductible_applied: number | string = 0,
			benefits_paid: number | string = 0;

		claimLines.forEach((claimLine: any) => {
			const { CHGAMT, EERSP, DEDAMT, COPMT, COINAMT, LNDISCAMT, ADJCVRD, PLNRESP } = claimLine;

			const notCovered = EERSP - DEDAMT - COPMT - COINAMT;
			if (notCovered) {
				not_covered = Number(not_covered) + notCovered;
			}
			if (LNDISCAMT) {
				discount = discount + LNDISCAMT;
			}
			if (CHGAMT) {
				total_charge = total_charge + CHGAMT;
			}
			if (ADJCVRD) {
				eligible_expense = eligible_expense + ADJCVRD;
			}
			if (DEDAMT) {
				deductible_applied = deductible_applied + DEDAMT;
			}
			if (PLNRESP) {
				benefits_paid = benefits_paid + PLNRESP;
			}
		});
		if (total_charge) {
			total_charge = dollarize(Number(total_charge));
		}
		if (discount) {
			discount = dollarize(Number(discount));
		}
		if (eligible_expense) {
			eligible_expense = dollarize(Number(eligible_expense));
		}
		if (not_covered) {
			not_covered = dollarize(Number(not_covered));
		}
		if (deductible_applied) {
			deductible_applied = dollarize(Number(deductible_applied));
		}
		if (benefits_paid) {
			benefits_paid = dollarize(Number(benefits_paid));
		}
		setBottomRowDate(
			Object.assign(
				{},
				{
					type_of_service: "TOTALS",
					service_from: "",
					service_thru: "",
					CHGAMT: total_charge,
					LNDISCAMT: discount,
					ADJCVRD: eligible_expense,
					not_covered,
					expl_codes: "",
					DEDAMT: deductible_applied,
					paid_at: "",
					PLNRESP: benefits_paid
				}
			)
		);
	}, [claimLines]);

	if (bottomRowData === null) return <></>;

	return (
		<div className="explanation-of-benefits-grid" id="explanation-of-benefits-grid">
			<div className="ag-grid-main-container">
				<div className="ag-theme-alpine" style={{ height: "350px", width: "100%" }}>
					<AgGridReact
						ref={agGridRef}
						rowData={claimLines}
						onGridReady={handleAgGridReady}
						animateRows
						rowDragManaged
						defaultColDef={{
							filter: "agTextColumnFilter",
							floatingFilter: true,
							resizable: true
						}}
						defaultColGroupDef={{ marryChildren: true }}
						rowSelection="single"
						enableCellChangeFlash={true}
						pinnedBottomRowData={[bottomRowData]}
						frameworkComponents={{
							dateCellRenderer: dateCellRenderer,
							amountCellRenderer: amountCellRenderer
						}}
					>
						<AgGridColumn field="TOS" headerName="Type of Service" suppressSizeToFit={true}></AgGridColumn>
						<AgGridColumn
							field="INCFRMDT"
							headerName="Service From"
							suppressSizeToFit={true}
							cellRenderer="dateCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							field="INCTODT"
							headerName="Service Thru"
							suppressSizeToFit={true}
							cellRenderer="dateCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							field="CHGAMT"
							headerName="Total Charge"
							suppressSizeToFit={true}
							cellRenderer="amountCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							valueGetter={notCoveredValueGetter}
							headerName="Not Covered"
							suppressSizeToFit={true}
							cellRenderer="amountCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							field="LNDISCAMT"
							headerName="Discount or Penalty"
							suppressSizeToFit={true}
							cellRenderer="amountCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							field="ADJCVRD"
							headerName="Eligible Expense"
							suppressSizeToFit={true}
							cellRenderer="amountCellRenderer"
						></AgGridColumn>
						<AgGridColumn
							//cellRenderer="remarkCodeCellRenderer"
							valueGetter={remarkCodevalueGetter}
							headerName="Rmk Cd"
							suppressSizeToFit={true}
						></AgGridColumn>
						<AgGridColumn
							field="DEDAMT"
							headerName="Deductible Applied"
							suppressSizeToFit={true}
							cellRenderer="amountCellRenderer"
						></AgGridColumn>
						<AgGridColumn field="PERC" headerName="Paid At%" suppressSizeToFit={true}></AgGridColumn>
						<AgGridColumn
							field="PLNRESP"
							headerName="Benfits Paid"
							suppressSizeToFit={true}
							cellRenderer="amountCellRenderer"
						></AgGridColumn>
					</AgGridReact>
				</div>
			</div>
		</div>
	);
};

export default ExplanationOfBenefitsGrid;
